import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { hasAnalyticsConsent } from '@/lib/analyticsConsent';
import { firstTouchFrom, keepFirstTouch, markFirstTouchSent, unsentFirstTouch } from '@/lib/analytics/firstTouch';
import { logger } from '@/lib/logger';

/**
 * First-touch attribution (src/lib/analytics/firstTouch.ts): keeps where
 * this visit came from once analytics consent is given (now, or when the
 * banner is answered later in the visit), and sends it once a person is
 * signed in. Without consent nothing is kept or sent.
 */
export function useFirstTouchAttribution(platform: 'web' | 'android'): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  useEffect(() => {
    const keep = () => {
      if (!hasAnalyticsConsent()) return;
      keepFirstTouch(window.localStorage, firstTouchFrom({
        search: window.location.search,
        pathname: window.location.pathname,
        referrer: document.referrer,
        host: window.location.hostname,
      }, new Date(), platform));
    };
    keep();
    const onConsent = (event: Event) => {
      if ((event as CustomEvent<{ analytics?: boolean }>).detail?.analytics === true) keep();
    };
    window.addEventListener('cookieConsentChanged', onConsent);
    return () => window.removeEventListener('cookieConsentChanged', onConsent);
  }, [platform]);

  useEffect(() => {
    if (!userId || !hasAnalyticsConsent()) return;
    const touch = unsentFirstTouch(window.localStorage);
    if (!touch) return;
    let cancelled = false;
    void (async () => {
      const { error } = await supabase.rpc('record_first_touch', { p_touch: { ...touch } });
      if (cancelled) return;
      if (error) {
        logger.warn('First-touch attribution was not recorded', error);
        return;
      }
      markFirstTouchSent(window.localStorage);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);
}
